/**
 * AinaConfirmSheet — polished modal for consequential AINA intents.
 * Renders:
 *   · What will happen (intent + module)
 *   · Who / what target (from slots.keywords)
 *   · Price if slots.max_price/min_price present
 *   · Location if slots.city / slots.lat present
 *   · Confirm + Cancel
 */
import React from "react";
import { X, ShieldCheck, MapPin, Coins, Sparkles, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AinaConfirmSheet({ decision, onConfirm, onCancel }) {
  if (!decision) return null;
  const isEmergency = decision.module === "Emergency";
  const price =
    decision.slots?.max_price ? `up to ₹${decision.slots.max_price}` :
    decision.slots?.min_price ? `from ₹${decision.slots.min_price}` : null;
  const location = decision.slots?.city
    ? decision.slots.city
    : (decision.slots?.lat && decision.slots?.lng
        ? `${decision.slots.lat.toFixed(3)}, ${decision.slots.lng.toFixed(3)}`
        : null);
  const kw = decision.slots?.keywords || "";
  return (
    <div className="fixed inset-0 z-[85] bg-black/60 grid place-items-center p-4"
         onClick={onCancel} data-testid="aina-confirm-sheet">
      <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <div className={`px-4 py-3 flex items-center justify-between ${isEmergency ? "bg-rose-600 text-white" : "bg-emerald-700 text-white"}`}>
          <div className="font-black flex items-center gap-2">
            {isEmergency ? <AlertTriangle size={18}/> : <Sparkles size={16}/>}
            {isEmergency ? "🚨 EMERGENCY" : "Confirm with AINA"}
          </div>
          <button onClick={onCancel} className="text-white/80 hover:text-white" data-testid="aina-confirm-close"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-sm text-stone-700" data-testid="aina-confirm-text">
            {decision.confirmation_text || `Proceed to ${decision.module}?`}
          </div>
          <div className="rounded-xl border border-stone-200 divide-y">
            <Row label="What"     value={`${(decision.intent||"").toUpperCase()} · ${decision.module}`} icon={ShieldCheck}/>
            {kw && <Row label="Target"  value={kw} icon={Sparkles}/>}
            {price && <Row label="Price" value={price} icon={Coins}/>}
            {location && <Row label="Where" value={location} icon={MapPin}/>}
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={onCancel} className="flex-1 bg-stone-100 text-stone-800 hover:bg-stone-200 font-bold" data-testid="aina-confirm-cancel">
              Cancel
            </Button>
            <Button onClick={onConfirm}
                    className={`flex-1 font-black ${isEmergency ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"} text-white`}
                    data-testid="aina-confirm-go">
              {isEmergency ? "Open Emergency" : "Confirm"}
            </Button>
          </div>
          <div className="text-[11px] text-stone-400 pt-1">
            AINA never executes bookings, payments, calls or shares until you confirm.
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="px-3 py-2 flex items-center gap-2">
      <Icon size={14} className="text-stone-400"/>
      <div className="text-[11px] uppercase tracking-widest text-stone-400 w-16">{label}</div>
      <div className="text-sm font-semibold text-stone-800 truncate">{value}</div>
    </div>
  );
}
